import { create } from 'zustand';
import { client } from '../graphql/client';
import { CURRENT_USER_WATERMARK } from '../graphql/queries/currentUserWatermark';
import { getUserInfo } from '../utils/auth';
import { WatermarkInfo, getClientTimeZone } from '../utils/watermark';

/**
 * 水印信息的内存态。
 *
 * **刻意不落 localStorage**（与 dataPortalStore 不同）：水印规范要求字段由后端在会话中下发，
 * 前端不得从 URL / localStorage 等可篡改来源拼装。缓存进 localStorage 就等于给了一个
 * 「改掉别人的工号再截图」的入口，与本需求的溯源目的直接冲突。
 *
 * 代价是刷新后首帧没有水印，直到 query 返回 —— 这个窗口和首屏数据渲染的窗口是同一个量级，
 * 不会出现「先有数据、后有水印」的截图窗口。
 */
export interface WatermarkState {
    info: WatermarkInfo | null;
    /** 拉取水印信息；失败时静默降级（不渲染水印），不阻塞任何业务流程 */
    refresh: () => Promise<void>;
}

// 不需要 clear()：utils/auth 的 logout 用 window.location.href 整页重载，store 随之销毁

interface WatermarkResponse {
    currentUserWatermark: WatermarkInfo;
}

export const useWatermarkStore = create<WatermarkState>((set) => ({
    info: null,

    refresh: async () => {
        // 未登录（登录页 / 教学视频页）直接跳过，不打无谓的请求
        if (getUserInfo()?.account?.id == null) {
            set({ info: null });
            return;
        }
        const result = await client.query<WatermarkResponse>({
            query: CURRENT_USER_WATERMARK,
            // 上送浏览器时区，后端按该时区取当日日期，解析不了才回落美西。
            // 日期本身仍由后端按服务端时钟算，端上只决定「按哪个时区看这一刻」。
            variables: { timezone: getClientTimeZone() },
            fetchPolicy: 'network-only',
        });
        set({ info: result.data?.currentUserWatermark ?? null });
    },
}));
